import { IconArrowBackUp, IconDownload, IconFile, IconFileMusic, IconFileSpreadsheet, IconFileText, IconFileTypePdf, IconFileWord, IconFileZip, IconLanguage, IconPhoneOutgoing, IconSend2, IconSparkles, IconUserCircle, IconVideo, IconX } from '@tabler/icons-react';
import mime from 'mime';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { toast } from 'sonner';
import { getApi, postApi } from '../../../../api/call.api';
import { urlApi } from '../../../../api/url.api';
import ConfirmationModal from '../../../../components/ConfirmationModal/ConfirmationModal';
import { socketKeys } from '../../../../statics/socketKeys';
import { formatMessage } from '../../../../utils/chat.utils';
import { chatDateTime } from '../../../../utils/dateTimeConverter';
import { renderTemplate } from '../../../../utils/renderCannedReply';
import AudioRecorderForChat from './AudioRecorderForChat';
import ChatHeader from './ChatHeader';
import FileSendInChat from './FileSendInChat';

const ChatBox = ({ selectedUser, socket, messageReceiveFunction, getUserList, setSelectedUser }) => {

    const [cannedRepliesList, setCannedRepliesList] = useState([]);
    const [page, setPage] = useState(1);
    const chatBoxRef = useRef(null);
    const [showSuggestionPopup, setShowSuggestionPopup] = useState(false);
    const [allMessages, setAllMessages] = useState([]);
    const [message, setMessage] = useState('');
    const [hasMore, setHasMore] = useState(true);
    const [loading, setLoading] = useState({ messages: false });
    const [isTyping, setIsTyping] = useState(false);
    const typingTimeoutRef = useRef(null);
    const [typingData, setTypingData] = useState(null);
    const [replyingMessage, setReplyingMessage] = useState(null);
    const [isChatSessionEnd, setIsChatSessionEnd] = useState(selectedUser?.status);
    const [beautifyLoading, setBeautifyLoading] = useState(false);
    const [beautifyData, setBeautifyData] = useState(null);
    const [selectedCannedReply, setSelectedCannedReply] = useState(null);
    const [endByType, setEndByType] = useState(selectedUser?.endByType)

    useEffect(() => {
        if (!socket) return;

        socket.on(socketKeys.RECEIVE_MESSAGE, (msg) => {
            messageReceiveFunction(msg?.chatSessionId, msg);
            onReceiveMessage(msg);
        });

        socket.on(socketKeys.ON_VISITOR_TYPING, (data) => {
            if (selectedUser?._id == data?.chatSessionId) {
                setIsTyping(true);
                setTypingData(data?.text);
                clearTimeout(typingTimeoutRef.current);
                typingTimeoutRef.current = setTimeout(() => {
                    setIsTyping(false);
                }, 1000); // 1 seconds of no typing = hide
                scrollToBottom()
            }
        });

        socket.on(socketKeys.ON_CHAT_SESSION_END, (data) => {
            if (selectedUser?._id == data?.chatSessionId) {
                setEndByType(data?.endByType)
                setIsChatSessionEnd(4);
            }
        })

        socket.on(socketKeys.ON_CHAT_SESSION_AUTO_END, (data) => {
            if (data?.chatSessionIds?.includes(selectedUser?._id)) {
                setEndByType(data?.endByType)
                setIsChatSessionEnd(4);
            }
        })

        return () => {
            socket.off(socketKeys.RECEIVE_MESSAGE);
            socket.off(socketKeys.TYPING);
            clearTimeout(typingTimeoutRef.current);
            setIsTyping(false);
            setTypingData(null);
            setReplyingMessage(null);
            setShowSuggestionPopup(false);
        };
    }, [socket, selectedUser, messageReceiveFunction]);

    const onReceiveMessage = (data) => {
        if (data) {
            if (selectedUser?._id == data?.chatSessionId) {
                setAllMessages((prev) => [...prev, data]);
                scrollToBottom();
            }
        }
    };

    useEffect(() => {
        setIsChatSessionEnd(selectedUser?.status);
        setEndByType(selectedUser?.endByType);
        return () => { }
    }, [selectedUser?.status, selectedUser?.endByType])


    useEffect(() => {
        getCannedReplies();
    }, []);

    const getCannedReplies = async () => {
        const resp = await getApi(urlApi.getCannedReplyList + "?filterByStatus=1");
        if (resp?.responseCode === 200) {
            setCannedRepliesList(resp?.data);
        } else {
            setCannedRepliesList([]);
        }
    };

    const sendMessage = useCallback((message, sessionId, messageType) => {
        if (!message?.trim()) return;
        if (socket) {
            let postData = {
                message: message,
                chatSessionId: sessionId,
                messageType: selectedCannedReply?.messageType || messageType,
            }
            if (selectedCannedReply?.messageType === 2 && selectedCannedReply?.file) {
                postData.file = selectedCannedReply?.file;
            }
            if (selectedCannedReply?.messageType === 3 && selectedCannedReply?.voice) {
                postData.voice = selectedCannedReply?.voice;
            }
            if (replyingMessage) {
                postData.parentMessageId = replyingMessage?.messageId;
            }
            socket.emit(socketKeys.SEND_MESSAGE, postData);
            setReplyingMessage(null);
            setBeautifyData(null);
            setMessage("");
        }
    }, [socket, replyingMessage, selectedCannedReply]);

    const getOldMessage = async () => {
        if (!selectedUser) return;

        const el = chatBoxRef.current;
        if (!el) return;

        const prevScrollHeight = el.scrollHeight;

        setLoading((prev) => ({ ...prev, messages: true }));

        const resp = await getApi(`${urlApi.getOldMessages}${selectedUser?._id}?page=${page}&limit=25`);

        setLoading((prev) => ({ ...prev, messages: false }));

        if (resp?.responseCode === 200 && Array.isArray(resp?.data)) {
            if (resp.data.length === 0) {
                setHasMore(false);
                return;
            }

            requestAnimationFrame(() => {
                // Prevent duplicates
                setAllMessages((prev) => {
                    const messageIds = new Set(prev.map(msg => msg._id));
                    const newMessages = resp.data.filter(msg => !messageIds.has(msg._id));
                    return [...newMessages, ...prev];
                });

                requestAnimationFrame(() => {
                    const newScrollHeight = el.scrollHeight;
                    el.scrollTop = newScrollHeight - prevScrollHeight;
                });
            });
        } else {
            setHasMore(false);
        }
    };


    // Reset chat when user changes
    useEffect(() => {
        if (selectedUser) {
            setPage(1);
            setHasMore(true);
            setAllMessages([]);
        }
    }, [selectedUser]);

    // Trigger load on page change
    useEffect(() => {
        if (selectedUser && page > 0) {
            getOldMessage();
        }
    }, [page, selectedUser]);

    // Scroll to bottom on first load
    useEffect(() => {
        if (page === 1) {
            setTimeout(() => scrollToBottom(), 50);
        }
    }, [allMessages, page]);


    const handleKeydown = (e) => {
        const localMessage = e?.target?.value;

        // Handle Enter + Shift (insert newline)
        if (e.key === 'Enter' && e.shiftKey) {
            return; // allow default behavior (newline)
        }

        // Handle Enter (send message)
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault(); // prevent newline

            const trimmedMessage = localMessage.trim();

            if (trimmedMessage === "/") return;

            if (trimmedMessage !== '') {
                sendMessage(trimmedMessage, selectedUser?._id, 1);
                setMessage('');
                e.target.value = '';
            }
        }

        // Typing event emit (moved here to avoid emitting on Enter key)
        socket.emit(socketKeys.TYPING, {
            chatSessionId: selectedUser?._id,
        });
    };



    const handleOnChange = (e) => {
        const val = e.target.value;
        setMessage(val);
        setShowSuggestionPopup(val === "/");
    };

    const handleChatScroll = (e) => {
        const threshold = 200;
        if (e.target.scrollTop <= threshold && hasMore && !loading.messages) {
            setPage((prev) => prev + 1);
        }
    };

    const scrollToBottom = () => {
        const el = chatBoxRef.current;
        if (el) {
            el.scrollTop = el.scrollHeight;
        }
    };

    const getBeautifyMsg = async () => {
        setBeautifyLoading(true);
        setBeautifyData(null);
        if (message?.length < 3 || message?.length > 1000) {
            toast.error("Minimum 3 and maximum 1000 characters are allowed for beautify!");
            setBeautifyLoading(false)
            return;
        }
        let postData = { message: message }
        let resp = await postApi(postData, urlApi.getBeautifyMsg);
        setBeautifyLoading(false);
        if (resp?.responseCode === 200) {
            setBeautifyData(resp?.data);
        } else {
            toast.error(resp?.message);
            setBeautifyData(null)
        }
    }

    return (
        <div className="h-full bg-gray-100 p-2 pt-0 flex-1">
            <div className="flex flex-col">
                {selectedUser && <ChatHeader selectedUser={selectedUser} setSelectedUser={(data) => setSelectedUser(data)} socket={socket} callback={() => getUserList?.()} />}

                <div
                    className={`text-lg p-2 overflow-y-auto h-[calc(100vh-260px)] ${replyingMessage && '!pb-12'}`}
                    ref={chatBoxRef}
                    onScroll={handleChatScroll}
                >
                    {loading.messages && page === 1 ? (
                        <div className='h-full flex flex-col gap-3 items-center justify-center text-xs'>
                            <Spinner size='sm' />
                            <p>Loading...</p>
                        </div>
                    ) : (
                        allMessages.map((item, i) => (
                            <ChatMessage key={item._id || i} item={item} onReplyClick={(data) => setReplyingMessage(data)} />
                        ))
                    )}
                    {isTyping && (
                        <div className='flex flex-col min-w-[100px] w-max max-w-[300px] !px-3 !py-1 mb-2 rounded-xl'>
                            <p className='text-gray-500 text-[10px] !mb-0 leading-snug break-words'>Typing...</p>
                            <p className='text-sm self-end leading-none'>{typingData}</p>
                        </div>
                    )}
                </div>

                {(selectedUser && (isChatSessionEnd == 1 || isChatSessionEnd == 2 || isChatSessionEnd == 5)) ? (
                    <div className="relative p-1 w-full h-max">
                        {showSuggestionPopup && (
                            <div className="absolute -top-[200px] left-0 w-full bg-white border max-h-[200px] overflow-y-auto border-gray-300 rounded-lg shadow-lg p-2 z-10">
                                <p className="text-sm text-gray-600 font-medium mb-1">Commands</p>
                                {cannedRepliesList?.map((item, index) => (
                                    <div
                                        key={index}
                                        className='hover:bg-gray-100 px-2 py-1 rounded cursor-pointer mb-1'
                                        onClick={() => {
                                            setSelectedCannedReply(item);
                                            setMessage(renderTemplate(item?.message, { user: selectedUser }));
                                            document.getElementById('messageInput')?.focus();
                                            setShowSuggestionPopup(false);
                                        }}
                                    >
                                        <p className='text-sm mb-0'>/ {item?.message}</p>
                                        {item?.file && item?.messageType == 2 && <a href={item?.file} target='_blank' rel="noreferrer" className='!mb-1.5 border-1 border-orange-500 p-0.5 rounded-md me-3 text-xs'>File Attached</a>}
                                        {item?.voice && item?.messageType == 3 && <a href={item?.voice} target='_blank' rel="noreferrer" className='!mb-1.5 border-1 border-teal-500 p-0.5 rounded-md text-xs'>Audio Attached</a>}
                                        {/* map tags  */}
                                        <div className='flex gap-1 mt-1'>
                                            {item?.tags?.map((tag, index) => (
                                                <span key={index} className='text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full'>{tag}</span>
                                            ))}
                                        </div>

                                    </div>
                                ))}
                            </div>
                        )}

                        {(beautifyData || beautifyLoading) &&
                            <div className="absolute -top-[125px] h-full left-0 w-full bg-white border max-h-[125px] border-gray-300 rounded-lg shadow-lg p-2 z-10">
                                <div className='flex justify-between items-center'> <p className="text-xs text-gray-500 italic font-medium mb-2 flex items-center !gap-2">Beautify Message <IconSparkles size={18} /></p>
                                    <button className='!p-1.5 ms-auto hover:bg-gray-100 rounded-full hover:text-gray-500 hover:cursor-pointer transition' onClick={() => setBeautifyData(null)}> <IconX stroke={2} size={18} /> </button>
                                </div>
                                {(beautifyData) ? <div className='flex items-end !gap-3'>
                                    <div className='max-h-[80px] overflow-y-auto'>
                                        <p className='text-sm font-medium'>{beautifyData}</p></div>
                                    <button
                                        className='p-2 ms-auto hover:bg-gray-100 h-max rounded-full hover:text-gray-800 hover:cursor-pointer transition'
                                        onClick={() => sendMessage(beautifyData, selectedUser?._id, 1)}
                                    >
                                        <IconSend2 size={20} />
                                    </button>
                                </div> : <div role="status" className="max-w-full animate-pulse">
                                    <div className="h-3 bg-gray-200 rounded-full dark:bg-gray-700 w-full !mb-2"></div>
                                    <div className="h-3 bg-gray-200 rounded-full dark:bg-gray-700 w-64 !mb-2"></div>
                                    <div className="h-3 bg-gray-200 rounded-full dark:bg-gray-700 w-48 !mb-2"></div>
                                    <div className="h-3 bg-gray-200 rounded-full dark:bg-gray-700 w-72 !mb-2"></div>
                                </div>}
                            </div>
                        }

                        <div className='absolute right-4 bottom-4'>
                            {message ? (
                                <div className='flex gap-2 items-center'>
                                    <button
                                        className='p-2 hover:bg-gray-200 bg-gray-100 rounded-full hover:text-gray-800 hover:cursor-pointer transition' title='Beautify'
                                        onClick={() => getBeautifyMsg(message, selectedUser?._id, 1)}
                                    >
                                        <IconSparkles size={20} />
                                    </button>
                                    <button
                                        className='p-2 hover:bg-gray-200 bg-gray-100 rounded-full hover:text-gray-800 hover:cursor-pointer transition'
                                        onClick={() => sendMessage(message, selectedUser?._id, 1)}
                                    >
                                        <IconSend2 size={20} />
                                    </button>
                                </div>
                            ) : (
                                <div className='flex gap-2'>
                                    <ConfirmationModal buttonComponent={<button
                                        className='p-2 hover:bg-gray-100 rounded-full hover:text-gray-500 hover:cursor-pointer text-white transition'>
                                        <IconPhoneOutgoing size={20} className='text-gray-800' />
                                    </button>}
                                        confirmationText={'Are you sure want to send call option to visitor?'}
                                        onConfirm={() => sendMessage("For better assistance , you can call us now.", selectedUser?._id, 4)}
                                    />
                                    <FileSendInChat socket={socket} visitorData={selectedUser} />
                                    <AudioRecorderForChat socket={socket} visitorData={selectedUser} />
                                </div>
                            )}
                        </div>

                        {replyingMessage && (
                            <div className={`absolute border-s-4 border-e -top-12 border-t ${replyingMessage?.senderType == 1 ? '!border-primary' : 'border-orange-600'} w-[99%] bg-gray-200 text-black !px-3 !py-1 max-h-full h-12 flex justify-between items-center gap-3`}>
                                <div className='flex justify-between w-full'>
                                    <div>
                                        <p className='text-[10px] leading-none'>{replyingMessage?.senderType == 1 ? "You" : "Visitor"}</p>
                                        <p className='text-sm !mb-0 leading-snug break-words max-w-[500px] max-line-1'>{formatMessage(replyingMessage?.message)}</p>
                                    </div>
                                    {replyingMessage?.messageType == 2 && <img src={replyingMessage?.file} alt="File" className='h-12 w-12 rounded-sm object-cover' />}
                                </div>
                                <button className=' hover:bg-gray-100 rounded-full p-1' onClick={() => setReplyingMessage(null)}>
                                    <IconX size={20} />
                                </button>
                            </div>
                        )}

                        <textarea
                            id="messageInput"
                            className='w-full p-2 outline-none resize-none rounded-lg shadow'
                            value={message}
                            rows={4}
                            placeholder='Type "/" for canned replies'
                            onChange={handleOnChange}
                            autoFocus
                            onKeyDown={handleKeydown}
                            data-gramm="false"
                        ></textarea>
                    </div>
                ) :
                    <div className={`text-center text-sm ${endByType == 1 ? '!text-primary' : endByType == 2 ? 'text-orange-600' : 'text-sky-600'}`}>
                        <p>--------- This Chat Session Is Ended By {endByType == 1 ? "Admin" : endByType == 2 ? "Visitor" : "Auto Timeout"} ----------</p>
                    </div>
                }
            </div>
        </div>
    );
};

export default ChatBox;


const ChatMessage = React.memo(({ item, onReplyClick }) => {
    const [isHovered, setIsHovered] = useState(false);
    const [currentMessage, setCurrentMessage] = useState(null);
    return (
        <div className={`${(item?.senderType == 1 || item?.senderType == 3) ? '!ps-9 ms-auto' : '!pe-9'} w-max message-list`}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}>

            <div className={`${(item?.senderType == 1 || item?.senderType == 3) ? '!bg-primary' : 'bg-[#424242]'} flex flex-col min-w-[150px] w-max max-w-[400px] !px-3 !py-1.5 mb-3 rounded-xl relative`}
            >
                {item?.senderType == 3 &&
                    <div className='flex items-center gap-2 !mb-1'>
                        <IconSparkles size={18} className='text-green-600' />
                        <p className='text-[10px] font-semibold text-green-600'>AI Generated</p>
                    </div>}
                {item?.senderType == 1 && <div className='flex items-center !gap-1 !mb-1'>
                    <IconUserCircle size={18} className='text-orange-600' />
                    <p className='text-xs text-orange-700 font-semibold'>{item?.senderName}</p></div>}
                {/* replying message */}
                {item?.parentMessage && <div className={`border-s-4 mb-1.5 overflow-hidden ${item?.parentMessage?.senderType == 1 ? 'border-sky-800 bg-blue-900' : 'border-[#2b2b2b] bg-[#424242]'} !py-1 !px-2 rounded-md flex justify-between gap-2 items-center`}>
                    <div>
                        <p className='text-[10px] text-gray-200 self-end leading-none'>{item?.parentMessage?.senderType == 1 ? "Admin" : "Visitor"}</p>
                        <p className='text-white text-sm !mb-0 leading-snug break-words'>{(item?.parentMessage?.message)}</p>
                    </div>
                    {item?.parentMessage?.messageType == 2 && <img src={item?.parentMessage?.file} alt="File" className='h-12 w-12 object-cover rounded-md' />}
                </div>}

                <CheckFileType item={item} />
                <p className='text-white text-sm !mb-1 leading-snug break-words'>{
                    item?.senderType == 1 ? formatMessage(currentMessage?.messageId == item?.messageId ? (item?.translatedMessage ? item?.translatedMessage : item?.message) : item?.message) :
                        formatMessage(currentMessage?.messageId == item?.messageId ? item?.message : (item?.translatedMessage ? item?.translatedMessage : item?.message))}</p>
                <p className='text-[10px] text-gray-200 self-end leading-none'>{chatDateTime(item?.createdAt)}</p>

                {(item?.translatedMessage && item?.messageType == 1) && <button className='flex items-center !gap-2 text-xs border border-1 text-white border-white w-max mx-auto rounded-md !mt-2.5 !mb-2 py-0.5 px-2.5' onClick={() => setCurrentMessage(currentMessage?.messageId == item?.messageId ? null : item)}> <IconLanguage size={18} />{` See ${currentMessage?.messageId == item?.messageId ? 'Translated' : 'Original'} Message `}</button>}

                {(isHovered && (item?.messageType == 2)) && (
                    <div className="absolute aspect-square w-8 h-8 flex right-2 justify-center items-center bg-gray-500 rounded-full text-white hover:bg-gray-600 transition-all duration-200 ease-in-out">
                        <a href={item?.file}> <IconDownload size={16} /> </a>
                    </div>
                )}

                {isHovered && (
                    <div className={`absolute top-1/2 ml-2 p-1.5 ${(item?.senderType == 1 || item?.senderType == 3) ? '-left-12' : '-right-12'} bg-gray-500 rounded-full text-white translate-y-[-50%] cursor-pointer hover:bg-gray-600 transition-all duration-200 ease-in-out`} onClick={() => onReplyClick(item)}>
                        <IconArrowBackUp size={16} />
                    </div>
                )}
            </div>
        </div>
    );
});

const CheckFileType = React.memo(({ item }) => {
    if (item?.messageType == 2 && item?.file) {
        return <FileIconReturnAccordingToMime fileLink={item?.file} />;
    } else if (item?.messageType == 3) {
        return <audio controls key={item?.messageId} className={`${(item?.senderType == 1 || item?.senderType == 3) ? 'admin-audio' : 'visitor-audio'} max-h-96 mx-auto rounded-md max-w-full`}>
            <source src={item?.voice} type="audio/mpeg" />
            Your browser does not support the audio element.
        </audio>;
    } else {
        return null;
    }
});

const ShowFullScreenImage = React.memo(({ src }) => {
    const [showFullScreenImage, setShowFullScreenImage] = useState(false);

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                setShowFullScreenImage(false);
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, []);

    return (
        <>
            <img src={src} alt="" className='max-w-full mx-auto rounded-md cursor-pointer' onClick={() => setShowFullScreenImage(true)} />
            {showFullScreenImage && (
                <div className='fixed top-0 left-0 w-full h-full bg-black bg-opacity-75 flex justify-center items-center z-[1050]'>
                    <img src={src} alt="" className='max-w-full max-h-full rounded-md' />
                    <button className='absolute top-4 right-4 text-white' onClick={() => setShowFullScreenImage(false)}> <IconX /> </button>
                </div>
            )}
        </>

    )
})

const FileIconReturnAccordingToMime = React.memo(({ fileLink }) => {

    const fileName = typeof fileLink === 'string'
        ? fileLink
        : fileLink?.name || fileLink?.url || '';

    const mimeType = mime.getType(fileName) || '';

    const renderIcon = () => {
        if (['image/jpeg', 'image/png', 'image/webp'].includes(mimeType)) {
            return <ShowFullScreenImage src={fileLink} />;
        }

        if (['application/pdf'].includes(mimeType)) return <IconFileTypePdf size={50} />;
        if (['application/vnd.ms-excel', 'text/csv', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'].includes(mimeType)) return <IconFileSpreadsheet size={50} />;
        if (['application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/msword'].includes(mimeType)) return <IconFileWord size={50} />;
        if (['text/plain'].includes(mimeType)) return <IconFileText size={50} />;
        if (['application/zip', 'application/x-7z-compressed', 'application/x-bzip', 'application/x-bzip2', 'application/gzip', 'application/vnd.android.package-archive'].includes(mimeType)) {
            return <IconFileZip size={50} />;
        }
        if (mimeType.startsWith('video/')) return <IconVideo size={50} />;
        if (mimeType.startsWith('audio/')) return <IconFileMusic size={50} />;

        return <IconFile size={50} />;
    };

    return (
        <span className="items-center">
            <div className='flex items-center justify-center text-6xl p-2'> {renderIcon()}</div>
            {!['image/jpeg', 'image/png', 'image/webp'].includes(mimeType) && <p className='text-white text-sm !mb-1 leading-snug break-words'>{fileLink?.split("/")[fileLink?.split("/")?.length - 1]}</p>}
        </span>
    );
});